@page "/learn/:step"
@import Code from "../components/Code.xzor"
@import { NavLink } from "@source-crafter/xzor-runtime"
@import { href } from "../routes"

@code {
  step = 1;
  readonly steps = [
    "A component and an app",
    "Components that talk",
    "Slots",
    "Cascading values",
    "Pages and routing",
    "Services",
    "Models",
    "More in templates",
  ];

  get title(): string {
    return this.steps[this.step - 1] ?? "";
  }

  protected override onParametersSet(): void {
    document.title = `${this.title} · xzor`;
    window.scrollTo?.(0, 0);
  }
}

<div class="learn">
  <aside>
    <span class="label">Learn xzor</span>
    <ol>
      @for (let i = 1; i <= steps.length; i++) {
        <li><NavLink href="@href.Learn({ step: i })" match="exact">@steps[i - 1]</NavLink></li>
      }
    </ol>
  </aside>

  <article class="page">
    <span class="label">Step @step of @steps.length</span>
    <h1>@title</h1>
    @switch (step) {
      case 1:
        <p>A component has two parts: <strong><code>@@code</code></strong> holds its state and methods, and <strong>the template</strong> (everything else) is the HTML it renders.</p>
        <Code name="hero" />
        <p>The entry point says which component to show and where:</p>
        <Code name="main-counter" />
        <Code name="index-html" />
        <ul>
          <li><code>@@count</code> prints a value. Use <code>@@(a + b)</code> for anything longer than a name or a member chain.</li>
          <li>You write <code>count</code>, not <code>this.count</code>: names that refer to <code>@@code</code> members get <code>this.</code> added for you.</li>
          <li><code>@@onclick</code> gets a typed DOM event. The fields it changes update the parts of the page that show them.</li>
          <li><code>@@if</code>, <code>@@for</code>, <code>@@while</code> and <code>@@switch</code> are ordinary TypeScript control flow around markup. <code>@@for</code> and <code>@@while</code> take an <code>else { }</code> for when nothing ran.</li>
        </ul>
        break;
      case 2:
        <p>A field in <code>@@code</code> is a <strong>prop</strong>: the parent sets it as an attribute. This child reports changes to its parent:</p>
        <Code name="stepper" />
        <p>And this is the parent:</p>
        <Code name="counter-bind" />
        <ul>
          <li>A capitalized tag is a component. A misspelled prop or a wrong type is a build error.</li>
          <li>In lists, add <code>@@key(item.id)</code> after the loop head: rows keep their DOM and state when they reorder or get a new object with the same key. Step 8 has the details.</li>
          <li>Lifecycle methods follow Blazor's order: <code>onInitialized</code>, <code>onParametersSet</code>, the render, <code>onAfterRender</code>, and <code>dispose()</code> when the component leaves the page. The first two have <code>Async</code> versions.</li>
        </ul>
        break;
      case 3:
        <p>The component decides <em>where</em> the content goes:</p>
        <Code name="card" />
        <p>The parent decides <em>what</em> goes there:</p>
        <Code name="card-use" />
        <p>A slot can pass data back: the component writes <code>@@slot:item(item)</code> and the parent writes <code>@@slot item(x)</code> with a body, with <code>x</code> typed for you.</p>
        break;
      case 4:
        <p>For things such as the theme or the current user: provide a value once, and every component below can read it, at any depth, without passing props.</p>
        <Code name="cascade-provide" />
        <Code name="cascade-consume" />
        <p>When the value changes, the components reading it update.</p>
        break;
      case 5:
        <p>Give a component a URL with <code>@@page</code>. URL parameters fill the props with the same name, converted to the prop's type.</p>
        <Code name="user-page" />
        <p>Keep the routes in one file. It decides which layout each page renders in and what to show when nothing matches:</p>
        <Code name="routes" />
        <Code name="main-routes" />
        <p>Links are typed too:</p>
        <Code name="layout-nav" />
        <ul>
          <li><code>href.UserPage(...)</code> is generated from your pages: a link to a route that doesn't exist won't compile.</li>
          <li><code>NavLink</code> adds the <code>active</code> class while you're on its page.</li>
          <li>Tag routes (<code>@@page "/admin" as admin</code>) to give them their own layout.</li>
        </ul>
        <p>Served from a sub-path, such as GitHub Pages? Declare it once and routes, links and <code>NavLink</code> follow it. This site does exactly that.</p>
        <Code name="base-href" />
        break;
      case 6:
        <p>Declare your services once:</p>
        <Code name="services" />
        <p>Register them with the application:</p>
        <Code name="main-di" />
        <p>Ask for one with <code>@@inject</code>:</p>
        <Code name="inject" />
        <p>xzor reads your types and generates the plain <code>new Database(new ConsoleLogger())</code> code. A missing service, an ambiguous one or a dependency cycle is a build error with a clear message. Async factories, keyed services and disposal work too.</p>
        break;
      case 7:
        <p>A model is a class that knows what changed in it. Extend <code>Model</code> and write plain fields: the build makes each one watch itself.</p>
        <Code name="model-worker" />
        <p>Use it in a page. A component that reads a model draws again when the value changes, even if a service or a timer changed it.</p>
        <Code name="model-use" />
        <ul>
          <li><code>hasState(State.Modified)</code> tells if a model is new, changed or deleted. <code>changes</code> is what to send to the server, and <code>null</code> when nothing changed.</li>
          <li><code>revertChanges()</code> undoes the edits. <code>confirmChanges()</code> accepts them after you save.</li>
          <li><code>errors</code> and <code>hasErrors</code> come from the <code>@@Validate</code> checks.</li>
          <li>A model must extend <code>Model</code> directly, and the tsconfig <code>lib</code> needs <code>"ESNext.Decorators"</code>.</li>
        </ul>
        break;
      case 8:
        <p>The rest of the template language: keys, attributes from where they're decided, forms, async data, show and hide, motion, and ways to do less work.</p>
        <h2>Lists and keys</h2>
        <p>A loop keeps each row's DOM, and the components in it, while the row's key stays. A new object with the same key (an immutable update) keeps the row too.</p>
        <Code name="more-keys" />
        <ul>
          <li>Keys come from <code>@@key(expr)</code> after the loop head (or <code>@@key="expr"</code> on the body's only top element), a field's <code>@@Key("id")</code>, a model's <code>@@Key</code> property, or the item itself. A duplicate key is an error, and so is an element <code>@@key</code> that keys no row.</li>
        </ul>
        <h2>Attributes</h2>
        <Code name="more-attrs" />
        <ul>
          <li><code>@@attr:name="value"</code> inside a branch sets an attribute on the element around it while that branch shows. Ctrl+click goes to that element.</li>
          <li><code>class:name</code> toggles one class; <code>aria-*</code> writes <code>true</code> and <code>false</code> as text.</li>
        </ul>
        <h2>Forms</h2>
        <Code name="more-forms" />
        <p><code>@@bind</code> writes back on <code>change</code>, <code>@@bind:input</code> on every keystroke; <code>@@bind:debounce</code> waits for a pause, and <code>@@bind:after</code> runs once the value is written.</p>
        <h2>Async data</h2>
        <Code name="more-await" />
        <p>The request runs again only when an argument's value changes (here <code>query</code>), and only the newest one shows.</p>
        <h2>Show and hide</h2>
        <Code name="more-show" />
        <p><code>@@show:if</code> and <code>@@show:switch</code> hide the branch they leave instead of removing it, so it keeps its state and comes back at once. Hidden branches stay live; <code>@@pause</code> stops one (or <code>@@pause { }</code> a part of it) until it shows again.</p>
        <h2>Motion</h2>
        <Code name="more-leave" />
        <p>An element marked <code>@@leave</code> stays, with <code>data-leaving</code>, until its exit animation ends. Entering is plain CSS.</p>
        <h2>Doing less work</h2>
        <Code name="more-less" />
        <ul>
          <li>Getters are cached: one run per change, however many places read them. <code>@@Volatile</code> opts one out.</li>
          <li><code>@@once(expr)</code> and <code>@@once { }</code> are read once and never updated.</li>
          <li><code>@@async</code> loads a component the first time it's used; <code>@@cached</code> keeps it, DOM and all, when it leaves the page.</li>
          <li><code>xzor build --minify</code> ships each page and <code>@@async</code> component in a chunk of its own, writes a shell per page that preloads its chunks (<code>dist/pages</code>, listed in <code>dist/pages.json</code>), and adds <code>.br</code> and <code>.gz</code> copies of the text outputs. A server app also gets <code>dist/server.js</code>.</li>
        </ul>
        break;
    }

    <nav class="pager">
      @if (step > 1) {
        <a class="xz-button" data-variant="soft" data-tone="neutral" href="@href.Learn({ step: step - 1 })">Previous: @steps[step - 2]</a>
      }
      @if (step < steps.length) {
        <a class="xz-button primary" data-variant="solid" data-tone="accent" href="@href.Learn({ step: step + 1 })">Next: @steps[step]</a>
      } else {
        <a class="xz-button primary" data-variant="solid" data-tone="accent" href="@href.HowItWorks()">Next: how it works</a>
      }
    </nav>
  </article>
</div>
